import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, CardHeader, Checkbox, Chip, CircularProgress, Divider, FormControlLabel,
  Grid, IconButton, Link, MenuItem, Snackbar, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon, Refresh as RefreshIcon, Save as SaveIcon, Send as SendIcon, StickyNote2 as NoteIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { formatDateTime } from '../../utils/dateUtils';
import { AdminPageHeader, errorMessage } from '../../components/Admin/adminUi';
import {
  KIND_LABELS, KindChip, MessageBubble, PriorityChip, STATUS_LABELS, TicketStatusChip,
} from '../../components/Support/supportUi';

const KINDS = ['support', 'partnership', 'sales', 'bug', 'feature'];
const STATUSES = ['new', 'open', 'waiting', 'resolved', 'closed'];
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

const buildForm = (t) => ({
  status: t?.status || 'new',
  priority: t?.priority || 'normal',
  kind: t?.kind || 'support',
  assignee: t?.assignee || '',
  tags: t?.tags || [],
});

/** "organizationType" -> "Organization type" */
const humanize = (key) => {
  const s = String(key).replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const InfoRow = ({ label, children }) => (
  <Box sx={{ mb: 1.25 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
    <Box sx={{ typography: 'body2', wordBreak: 'break-word' }}>{children || '—'}</Box>
  </Box>
);

const AdminSupportTicket = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState('');

  const [form, setForm] = useState(buildForm(null));
  const [saving, setSaving] = useState(false);

  const [reply, setReply] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [afterStatus, setAfterStatus] = useState('keep');
  const [replying, setReplying] = useState(false);
  const [mailResult, setMailResult] = useState(null);

  const [note, setNote] = useState('');
  const [noting, setNoting] = useState(false);

  const applyTicket = (t) => {
    setTicket(t);
    setForm(buildForm(t));
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.admin.getSupportTicket(id);
      setTicket(res.data.ticket);
      setForm(buildForm(res.data.ticket));
    } catch (err) {
      setError(errorMessage(err, 'Failed to load ticket'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const original = useMemo(() => buildForm(ticket), [ticket]);
  const changes = useMemo(() => {
    const out = {};
    ['status', 'priority', 'kind'].forEach((k) => { if (form[k] !== original[k]) out[k] = form[k]; });
    if (form.assignee.trim() !== (original.assignee || '')) out.assignee = form.assignee.trim() || null;
    if (JSON.stringify(form.tags) !== JSON.stringify(original.tags)) out.tags = form.tags;
    return out;
  }, [form, original]);
  const dirty = Object.keys(changes).length > 0;

  const save = async () => {
    if (!dirty) return;
    try {
      setSaving(true);
      setError(null);
      const res = await apiService.admin.updateSupportTicket(id, changes);
      applyTicket({ ...ticket, ...res.data.ticket });
      setToast('Ticket updated');
    } catch (err) {
      setError(errorMessage(err, 'Failed to update ticket'));
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      setReplying(true);
      setError(null);
      setMailResult(null);
      const payload = { message: reply.trim(), sendEmail };
      if (afterStatus !== 'keep') payload.status = afterStatus;
      const res = await apiService.admin.replySupportTicket(id, payload);
      applyTicket({ ...ticket, ...res.data.ticket });
      setReply('');
      setAfterStatus('keep');
      setMailResult(res.data.mail || { sent: false, reason: sendEmail ? 'unknown' : 'email not requested' });
    } catch (err) {
      setError(errorMessage(err, 'Failed to send reply'));
    } finally {
      setReplying(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      setNoting(true);
      setError(null);
      const res = await apiService.admin.addSupportNote(id, note.trim());
      applyTicket({ ...ticket, ...res.data.ticket });
      setNote('');
    } catch (err) {
      setError(errorMessage(err, 'Failed to add note'));
    } finally {
      setNoting(false);
    }
  };

  if (loading && !ticket) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;
  }

  if (!ticket) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/support')} sx={{ mb: 2 }}>Back to support</Button>
        <Alert severity="error">{error || 'Ticket not found'}</Alert>
      </Box>
    );
  }

  const r = ticket.requester || {};
  const messages = [...(ticket.messages || [])].sort((a, b) => new Date(a.at) - new Date(b.at));
  const notes = [...(ticket.notes || [])].sort((a, b) => new Date(a.at) - new Date(b.at));
  const details = Object.entries(ticket.details || {});
  const companyName = r.companyName || r.company;

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/support')} sx={{ mb: 1 }}>Back to support</Button>
      <AdminPageHeader
        title={ticket.subject}
        subtitle={`${ticket.number} · opened ${formatDateTime(ticket.createdAt)}`}
        actions={<Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: -1.5, mb: 3 }}>
        <KindChip kind={ticket.kind} />
        <TicketStatusChip status={ticket.status} />
        <PriorityChip priority={ticket.priority} />
        {(ticket.tags || []).map((t) => <Chip key={t} label={`#${t}`} size="small" variant="outlined" />)}
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Conversation */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Conversation" titleTypographyProps={{ variant: 'h6' }} subheader={`${messages.length} message${messages.length === 1 ? '' : 's'}`} />
            <Divider />
            <CardContent>
              {messages.length === 0 && <Typography color="text.secondary">No messages.</Typography>}
              {messages.map((m) => (
                <MessageBubble
                  key={m.id || m.at}
                  mine={m.from === 'agent'}
                  author={m.from === 'agent' ? `${m.authorName || 'Excubia support'} (agent)` : (m.authorName || r.name || 'Requester')}
                  body={m.body}
                  at={m.at}
                />
              ))}
            </CardContent>
            <Divider />
            <CardContent>
              <TextField
                multiline minRows={4} maxRows={14} fullWidth placeholder={`Reply to ${r.name || r.email || 'the requester'}…`}
                value={reply} onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendReply(); }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1.5 }}>
                <FormControlLabel control={<Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />} label="Send email to requester" />
                <TextField select size="small" label="Set status after reply" value={afterStatus} onChange={(e) => setAfterStatus(e.target.value)} sx={{ minWidth: 200 }}>
                  <MenuItem value="keep">Keep current</MenuItem>
                  <MenuItem value="waiting">Waiting on requester</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                </TextField>
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="contained" onClick={sendReply} disabled={replying || !reply.trim()} startIcon={replying ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}>
                  Send
                </Button>
              </Stack>
              {mailResult && (
                <Alert severity={mailResult.sent ? 'success' : 'warning'} sx={{ mt: 1.5 }} onClose={() => setMailResult(null)}>
                  {mailResult.sent ? 'Reply saved and emailed to the requester.' : `Reply saved. Not emailed: ${mailResult.reason || mailResult.error || 'unknown reason'}`}
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader avatar={<NoteIcon color="warning" />} title="Internal notes" titleTypographyProps={{ variant: 'h6' }} subheader="Visible to Excubia staff only" />
            <Divider />
            <CardContent>
              {notes.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No notes yet.</Typography>}
              {notes.map((n) => (
                <Box key={n.id || n.at} sx={{ mb: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover', borderLeft: 3, borderColor: 'warning.main' }}>
                  <Typography variant="caption" color="text.secondary">{n.authorName || 'Staff'} · {formatDateTime(n.at)}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{n.body}</Typography>
                </Box>
              ))}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                <TextField multiline minRows={2} fullWidth size="small" placeholder="Add an internal note…" value={note} onChange={(e) => setNote(e.target.value)} />
                <Button variant="outlined" onClick={addNote} disabled={noting || !note.trim()} sx={{ whiteSpace: 'nowrap' }}>Add note</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Side column */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Requester" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent>
              <InfoRow label="Name">{r.name}</InfoRow>
              <InfoRow label="Email">{r.email && <Link href={`mailto:${r.email}?subject=${encodeURIComponent(`[${ticket.number}] ${ticket.subject}`)}`}>{r.email}</Link>}</InfoRow>
              <InfoRow label="Company">
                {r.companyId
                  ? <Link component={RouterLink} to={`/admin/customers/${r.companyId}`}>{companyName || r.companyId}</Link>
                  : companyName}
                {r.companyId && r.company && r.companyName && r.company !== r.companyName && (
                  <Typography variant="caption" color="text.secondary" display="block">Entered as: {r.company}</Typography>
                )}
              </InfoRow>
              <InfoRow label="Phone">{r.phone && <Link href={`tel:${r.phone}`}>{r.phone}</Link>}</InfoRow>
              <InfoRow label="Source">{ticket.source === 'app' ? 'In-app (signed in)' : 'Public website'}</InfoRow>
              <InfoRow label="Submitted">{formatDateTime(ticket.createdAt)}</InfoRow>
              {ticket.resolvedAt && <InfoRow label="Resolved">{formatDateTime(ticket.resolvedAt)}</InfoRow>}
              {(ticket.meta?.ip || ticket.meta?.userAgent) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}>
                  {ticket.meta?.ip && <>IP {ticket.meta.ip}<br /></>}
                  {ticket.meta?.userAgent}
                </Typography>
              )}
            </CardContent>
          </Card>

          {details.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardHeader title="Details" titleTypographyProps={{ variant: 'h6' }} />
              <Divider />
              <CardContent>
                {details.map(([k, v]) => (
                  <InfoRow key={k} label={humanize(k)}>
                    {Array.isArray(v)
                      ? <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>{v.map((x) => <Chip key={x} label={x} size="small" />)}</Stack>
                      : String(v)}
                  </InfoRow>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader title="Ticket" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <TextField select size="small" label="Status" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                  {STATUSES.map((s) => <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Priority" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
                  {PRIORITIES.map((p) => <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Kind" value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}>
                  {KINDS.map((k) => <MenuItem key={k} value={k}>{KIND_LABELS[k]}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Assignee" placeholder="Name or email" value={form.assignee} onChange={(e) => setForm((f) => ({ ...f, assignee: e.target.value }))} inputProps={{ maxLength: 200 }} />
                <Autocomplete
                  multiple freeSolo options={[]} value={form.tags}
                  onChange={(e, value) => setForm((f) => ({ ...f, tags: [...new Set(value.map((v) => String(v).trim().slice(0, 40)).filter(Boolean))].slice(0, 10) }))}
                  renderTags={(value, getTagProps) => value.map((option, index) => <Chip size="small" label={option} {...getTagProps({ index })} key={option} />)}
                  renderInput={(params) => <TextField {...params} size="small" label="Tags" placeholder="Type and press Enter" />}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button disabled={!dirty || saving} onClick={() => setForm(original)}>Reset</Button>
                  <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} disabled={!dirty || saving} onClick={save}>Save</Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
};

export default AdminSupportTicket;
